/**
 * `attack <actor> [with <item>]` — the player's strike (D234, actors round
 * D; `docs/design/actors.md` §6; Mike's rulings 2 and 3 of 2026-10-04).
 *
 * @remarks
 * The actor intercept has already decided the body is a FOE (it has a
 * `combat` block — ruling 3: attackable, because the loader proved its
 * hostility can come true) and within reach. What is left is the ladder
 * `actors.md` §6 writes, then the strike:
 *
 * 1. A fallen body is not struck again (`foeAlreadyDefeated`).
 * 2. `with <item>`: the thing must be carried (`notCarried`), must be a
 *    weapon (`notAWeapon`), and — D143's rule for the mask, read for a
 *    knife — must be wielded if it is wieldable (`weaponNotWielded`). With
 *    no `with`, the best thing in hand strikes, else bare hands
 *    (`strikingHandOf`).
 * 3. The strike: the weapon's damage less the foe's `defense`, floored
 *    (ruling 2 — no roll), spoken as the foe's `hurtNarratives` or the
 *    `strikeLanded` fact when it cost the foe something, `strikeAbsorbed`
 *    when it cost nothing; the body goes in the struck ledger either way
 *    (`actor_attacked` reads it, so a pack can make the strike the thing
 *    that turns the foe hostile).
 * 4. Defeat, when nothing is left: `defeatNarratives` or `foeDefeated`,
 *    then `onDefeat` in order, wherever effects live. The body stays where
 *    it fell.
 *
 * The player strikes FIRST (§6): the counter-strike is the tick's, after
 * this handler, so a killing blow lands before the foe answers it — and a
 * foe that has fallen answers nothing.
 *
 * @packageDocumentation
 */

import type { ItemId, RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import {
	combatOf,
	isActorDefeated,
	strikeDamageOf,
	type StrikingHand,
	strikingHandOf,
	withActorAttacked,
	withActorHealth,
} from '../entities/actor-combat.js';
import { type ActionHandlerResult, completedSpeaking, rejected, type SpeakableLine } from './action-handler-result.js';
import { type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

/** The strike's action. */
type AttackAction = Extract<RuntimeAction, { type: 'attack' }>;

/**
 * Strike a foe.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed strike: the foe, and the weapon named with `with`, if any
 * @param cursor - This turn's transcript cursor
 * @returns The strike, or the typed refusal from the ladder
 */
export const handleAttack = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: AttackAction,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const actor = requireActor(bundle, action.actorId);
	const combat = combatOf(actor);

	// The intercept narrows only a foe; a block-less body cannot reach here.
	if (combat === undefined) {
		return rejected(bundle, state, { actorId: action.actorId, type: 'not_a_foe' }, [
			speakSlot(bundle, state, 'notAFoe', { itemName: actor.name }),
		]);
	}

	if (isActorDefeated(bundle, state, action.actorId)) {
		return rejected(bundle, state, { actorId: action.actorId, type: 'foe_defeated' }, [
			speakSlot(bundle, state, 'foeAlreadyDefeated', { itemName: actor.name }),
		]);
	}

	const hand = action.weaponItemId === undefined ? strikingHandOf(bundle, state) : namedWeaponOf(bundle, state, action.weaponItemId);

	if ('rejection' in hand) {
		return hand;
	}

	const damageDealt = strikeDamageOf(hand.damage, combat.defense);
	// What is left before the blow: the record's, or — a first strike — the declared health (`healthOfActor`'s rule, with the block in hand).
	const remaining = Math.max(0, (state.world.actorHealth[action.actorId] ?? combat.health) - damageDealt);
	const struck = withActorHealth(bundle, withActorAttacked(state, action.actorId), action.actorId, remaining);
	const strikeLines: readonly SpeakableLine[] =
		damageDealt === 0
			? [speakSlot(bundle, state, 'strikeAbsorbed', { itemName: actor.name })]
			: combat.hurtNarratives ?? [speakSlot(bundle, state, 'strikeLanded', { damage: String(damageDealt), health: String(remaining), itemName: actor.name })];

	if (remaining > 0) {
		return completedSpeaking(bundle, struck, cursor, strikeLines, `attack:${action.actorId}`);
	}

	// The fall: its lines after the strike's, then what the fall does.
	const fallen = completedSpeaking(
		bundle,
		struck,
		cursor,
		[...strikeLines, ...(combat.defeatNarratives ?? [speakSlot(bundle, state, 'foeDefeated', { itemName: actor.name })])],
		`attack:${action.actorId}`,
	);

	return withEffects(bundle, fallen, combat.onDefeat);
};

/**
 * The weapon the player NAMED, judged by the ladder's second rung: carried,
 * a weapon, and wielded if wieldable.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param weaponItemId - The thing named after `with`
 * @returns The striking hand, or the refusal
 */
const namedWeaponOf = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	weaponItemId: ItemId,
): Extract<ActionHandlerResult, { type: 'rejected' }> | StrikingHand => {
	const item = requireItem(bundle, weaponItemId);

	if (!state.world.inventoryItemIds.includes(weaponItemId)) {
		return rejected(bundle, state, { itemId: weaponItemId, type: 'not_carried' }, [speakSlot(bundle, state, 'notCarried', { itemName: item.name })]);
	}

	if (item.weapon === undefined) {
		return rejected(bundle, state, { itemId: weaponItemId, type: 'not_a_weapon' }, [speakSlot(bundle, state, 'notAWeapon', { itemName: item.name })]);
	}

	if (item.equippable !== undefined && !Object.values(state.world.equippedItemIds).includes(weaponItemId)) {
		return rejected(bundle, state, { itemId: weaponItemId, type: 'not_equipped' }, [speakSlot(bundle, state, 'weaponNotWielded', { itemName: item.name })]);
	}

	return { damage: item.weapon.damage, weaponItemId };
};
